import React, { useCallback, useEffect, useState, Ref } from 'react';
import toast from 'react-hot-toast';
import { api, loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../../utils/buildingSelection';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  incomeExpenseApi,
  ProjectBankAccount,
  ProjectBankDepositLiquidity,
  ProjectBankFundPurpose,
} from '../../../api/incomeExpenseApi';
import { settingsApi, VietQRBankOption } from '../../../services/settingsApi';
import ProjectAccountLedgerDetail, { ProjectAccountLedgerDetailHandle } from './ProjectAccountLedgerDetail';

const formatCurrency = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n))}đ`;

const formatAmountInput = (value: number) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 }).format(Math.round(value));
};

const parseAmountInput = (value: string) => Number(value.replace(/[^\d]/g, '')) || 0;

const formatBankLabel = (bank: VietQRBankOption) => `${bank.shortName} - ${bank.name}`;

/** Keep in sync with backend `PROJECT_BANK_*_DEPOSIT_LABEL` (Tổng hợp thu chi section I / II / III). */
const DEPOSIT_LIQUIDITY_OPTIONS: Array<{ value: ProjectBankDepositLiquidity; label: string }> = [
  { value: 'DEMAND', label: 'Tiền gửi không kỳ hạn (KKH)' },
  { value: 'TERM', label: 'Tiền gửi có kỳ hạn (CKH)' },
];

const FUND_PURPOSE_OPTIONS: Array<{ value: ProjectBankFundPurpose; label: string }> = [
  { value: 'OPERATIONS', label: 'Quỹ vận hành' },
  { value: 'MAINTENANCE', label: 'Quỹ bảo trì' },
  { value: 'OTHER', label: 'Khác' },
];

type AccountForm = {
  accountType: string;
  depositLiquidity: ProjectBankDepositLiquidity | '';
  fundPurpose: ProjectBankFundPurpose | '';
  name: string;
  openingCashBalance: number;
  openingTransferBalance: number;
  exportVatInvoice: boolean;
  notes: string;
  bankAccountName: string;
  bankAccountNumber: string;
  bankName: string;
  bankBin: string;
  isDefault: boolean;
};

const emptyForm: AccountForm = {
  accountType: '',
  depositLiquidity: 'DEMAND',
  fundPurpose: 'OPERATIONS',
  name: '',
  openingCashBalance: 0,
  openingTransferBalance: 0,
  exportVatInvoice: false,
  notes: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankName: '',
  bankBin: '',
  isDefault: false,
};

const AmountInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  required?: boolean;
}> = ({ label, value, onChange, required }) => {
  const [display, setDisplay] = useState(formatAmountInput(value));

  useEffect(() => {
    setDisplay(formatAmountInput(value));
  }, [value]);

  return (
    <div>
      <label className="block text-sm text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) => {
          const raw = e.target.value;
          setDisplay(raw);
          onChange(parseAmountInput(raw));
        }}
        onBlur={() => setDisplay(formatAmountInput(value))}
        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
        placeholder="0"
      />
    </div>
  );
};

interface ProjectBankAccountsTabProps {
  ledgerDetailRef?: Ref<ProjectAccountLedgerDetailHandle>;
  onLedgerDetailOpenChange?: (open: boolean) => void;
}

export const ProjectBankAccountsTab: React.FC<ProjectBankAccountsTabProps> = ({
  ledgerDetailRef,
  onLedgerDetailOpenChange,
}) => {
  const { user, isSuperAdmin, isOperator } = useAuth();
  const canDelete = isSuperAdmin || isOperator;
  const [buildings, setBuildings] = useState<Array<{ id: string; name: string }>>([]);
  const [buildingId, setBuildingId] = useState('');
  const [accounts, setAccounts] = useState<ProjectBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<ProjectBankAccount | null>(null);
  const [form, setForm] = useState<AccountForm>(emptyForm);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailAccount, setDetailAccount] = useState<ProjectBankAccount | null>(null);
  const [banks, setBanks] = useState<VietQRBankOption[]>([]);

  useEffect(() => {
    settingsApi.getQrBanks().then(setBanks).catch(() => {
      toast.error('Không thể tải danh sách ngân hàng');
    });
  }, []);

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  const loadAccounts = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      setAccounts(await incomeExpenseApi.listProjectBankAccounts(buildingId));
    } catch {
      toast.error('Không thể tải tài khoản dự án');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const resolveBankSelection = (account: Pick<ProjectBankAccount, 'bankBin' | 'bankName'>) => {
    if (account.bankBin) {
      const bank = banks.find((item) => item.bin === account.bankBin);
      return {
        bankBin: account.bankBin,
        bankName: bank ? formatBankLabel(bank) : account.bankName || '',
      };
    }

    const matched = banks.find(
      (bank) =>
        formatBankLabel(bank) === account.bankName ||
        bank.shortName === account.bankName ||
        bank.name === account.bankName ||
        bank.code === account.bankName ||
        (bank.code && `${bank.code} - ${bank.name}` === account.bankName),
    );

    return {
      bankBin: matched?.bin || '',
      bankName: matched ? formatBankLabel(matched) : account.bankName || '',
    };
  };

  const openEdit = (account: ProjectBankAccount) => {
    const bankSelection = resolveBankSelection(account);
    setEditing(account);
    setForm({
      accountType: account.accountType || '',
      depositLiquidity: account.depositLiquidity ?? 'DEMAND',
      fundPurpose: account.fundPurpose ?? 'OPERATIONS',
      name: account.name,
      openingCashBalance: account.openingCashBalance,
      openingTransferBalance: account.openingTransferBalance,
      exportVatInvoice: account.exportVatInvoice,
      notes: account.notes || '',
      bankAccountName: account.bankAccountName || '',
      bankAccountNumber: account.bankAccountNumber || '',
      bankName: bankSelection.bankName,
      bankBin: bankSelection.bankBin,
      isDefault: account.isDefault,
    });
    setShowForm(true);
  };

  const handleBankChange = (bin: string) => {
    const bank = banks.find((item) => item.bin === bin);
    setForm((f) => ({
      ...f,
      bankBin: bin,
      bankName: bank ? formatBankLabel(bank) : '',
    }));
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn tòa nhà');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Vui lòng nhập tên tài khoản');
      return;
    }
    if (!form.depositLiquidity) {
      toast.error('Vui lòng chọn loại tiền gửi');
      return;
    }
    if (!form.fundPurpose) {
      toast.error('Vui lòng chọn quỹ');
      return;
    }
    if (!form.bankAccountNumber.trim() && form.bankBin) {
      toast.error('Vui lòng nhập số tài khoản');
      return;
    }
    if (form.bankAccountNumber.trim() && !form.bankBin) {
      toast.error('Vui lòng chọn ngân hàng để tạo mã QR thanh toán');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await incomeExpenseApi.updateProjectBankAccount(editing.id, {
          ...form,
          depositLiquidity: form.depositLiquidity,
          fundPurpose: form.fundPurpose,
        });
      } else {
        await incomeExpenseApi.createProjectBankAccount({
          buildingId,
          ...form,
          depositLiquidity: form.depositLiquidity,
          fundPurpose: form.fundPurpose,
        });
      }
      toast.success('Đã lưu tài khoản');
      closeForm();
      loadAccounts();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Không thể lưu tài khoản'));
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (account: ProjectBankAccount) => {
    setDetailId(account.id);
    setDetailAccount(account);
    onLedgerDetailOpenChange?.(true);
  };

  const handleDelete = async (account: ProjectBankAccount) => {
    if (!window.confirm(`Xóa tài khoản "${account.name}"?`)) return;
    try {
      await incomeExpenseApi.deleteProjectBankAccount(account.id);
      toast.success('Đã xóa tài khoản');
      loadAccounts();
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, 'Không thể xóa tài khoản'));
    }
  };

  if (detailId && detailAccount) {
    return (
      <ProjectAccountLedgerDetail
        ref={ledgerDetailRef}
        account={detailAccount}
        buildingId={buildingId}
        buildingName={buildings.find((b) => b.id === buildingId)?.name}
        onBack={() => {
          setDetailId(null);
          setDetailAccount(null);
          onLedgerDetailOpenChange?.(false);
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {shouldShowBuildingSelector(buildings) && (
        <select value={buildingId} onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)} {...getBuildingSelectProps(buildings)} className="rounded-lg border px-3 py-2 text-sm">
          {needsBuildingSelection(buildings) && <option value="">-- Chọn tòa nhà --</option>}
          {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        )}
        <button type="button" onClick={openCreate} className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium">+ Thêm tài khoản</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing ? 'Sửa tài khoản' : 'Thêm tài khoản'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Loại tiền gửi<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <select
                    value={form.depositLiquidity}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        depositLiquidity: e.target.value as ProjectBankDepositLiquidity,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    {DEPOSIT_LIQUIDITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Quỹ<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <select
                    value={form.fundPurpose}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        fundPurpose: e.target.value as ProjectBankFundPurpose,
                      }))
                    }
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    {FUND_PURPOSE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Tên tài khoản<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    placeholder="Nhập tên tài khoản"
                  />
                </div>
                <AmountInput
                  label="Số tiền gốc tiền mặt"
                  value={form.openingCashBalance}
                  onChange={(openingCashBalance) => setForm((f) => ({ ...f, openingCashBalance }))}
                  required
                />
                <AmountInput
                  label="Số tiền gốc chuyển khoản"
                  value={form.openingTransferBalance}
                  onChange={(openingTransferBalance) => setForm((f) => ({ ...f, openingTransferBalance }))}
                  required
                />
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.exportVatInvoice}
                    onChange={(e) => setForm((f) => ({ ...f, exportVatInvoice: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Xuất hoá đơn VAT
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Tài khoản mặc định
                </label>
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Ghi chú</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Ghi chú"
                />
              </div>

              <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 space-y-4">
                <div className="text-sm font-medium text-gray-800">Thông tin tài khoản ngân hàng</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Tên chủ tài khoản</label>
                    <input
                      value={form.bankAccountName}
                      onChange={(e) => setForm((f) => ({ ...f, bankAccountName: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                      placeholder="Tên chủ tài khoản ngân hàng"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Số tài khoản</label>
                    <input
                      value={form.bankAccountNumber}
                      onChange={(e) => setForm((f) => ({ ...f, bankAccountNumber: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                      placeholder="Số tài khoản"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-gray-700 mb-1">Ngân hàng</label>
                    <select
                      value={form.bankBin}
                      onChange={(e) => handleBankChange(e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                    >
                      <option value="">-- Chọn ngân hàng --</option>
                      {banks.map((bank) => (
                        <option key={bank.bin} value={bank.bin}>
                          {formatBankLabel(bank)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Tên tài khoản</th>
              <th className="px-4 py-3 text-left">Tên chủ tài khoản</th>
              <th className="px-4 py-3 text-left">Số tiền gốc</th>
              <th className="px-4 py-3 text-center" colSpan={3}>Số dư</th>
              <th className="px-4 py-3 text-left">Người tạo</th>
              <th className="px-4 py-3 text-left">Thao tác</th>
            </tr>
            <tr className="bg-gray-50 text-xs text-gray-500">
              <th colSpan={3}></th>
              <th className="px-4 py-1">Ngân hàng</th>
              <th className="px-4 py-1">Tiền mặt</th>
              <th className="px-4 py-1">Tổng cộng</th>
              <th colSpan={2}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : accounts.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Chưa có tài khoản</td></tr>
            ) : accounts.map((account) => (
              <tr key={account.id} className="border-t">
                <td className="px-4 py-3">
                  <div className="font-medium">{account.name}</div>
                  <div className="text-xs text-gray-500">
                    {DEPOSIT_LIQUIDITY_OPTIONS.find((o) => o.value === account.depositLiquidity)?.label
                      ?? 'Tiền gửi không kỳ hạn (KKH)'}
                    {' · '}
                    {FUND_PURPOSE_OPTIONS.find((o) => o.value === account.fundPurpose)?.label
                      ?? 'Quỹ vận hành'}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>{account.bankAccountName || '—'}</div>
                  {account.bankAccountNumber ? (
                    <div className="text-xs text-gray-500">{account.bankAccountNumber}</div>
                  ) : null}
                  {account.bankName ? (
                    <div className="text-xs text-gray-500">
                      {resolveBankSelection(account).bankName || account.bankName}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <div>TM: {formatCurrency(account.openingCashBalance)}</div>
                  <div>CK: {formatCurrency(account.openingTransferBalance)}</div>
                </td>
                <td className="px-4 py-3 text-right">{formatCurrency(account.transferBalance)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(account.cashBalance)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(account.cashBalance + account.transferBalance)}</td>
                <td className="px-4 py-3">{account.creator?.name || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => openDetail(account)} className="text-cyan-600" title="Xem">👁</button>
                    <button type="button" onClick={() => openEdit(account)} className="text-orange-500" title="Sửa">✏️</button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(account)}
                        className="text-red-500 hover:text-red-600"
                        title="Xóa"
                      >
                        🗑️
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        await incomeExpenseApi.updateProjectBankAccount(account.id, { isInternal: !account.isInternal });
                        loadAccounts();
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${account.isInternal ? 'bg-gray-400' : 'bg-cyan-500'}`}
                      title="Nội bộ"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white ${account.isInternal ? 'translate-x-1' : 'translate-x-6'}`} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectBankAccountsTab;
